import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Play, Captions, CaptionsOff } from "lucide-react";
import { useSfx } from "@/hooks/useSfx";
import { musicController } from "@/lib/audio/musicController";
import { useLessonCaptions } from "@/hooks/useLessonCaptions";
import LessonClosedCaptions from "@/components/lessons/LessonClosedCaptions";
import { useLessonVoiceMoment } from "@/hooks/useLessonVoiceMoment";
import { getLessonMomentFor } from "@/lib/audio/lessonVoiceMap";


interface VideoStageProps {
  videoUrl: string;
  lessonName: string;
  lessonId?: string;
  onContinue: () => void;
}

const VideoStage = ({ videoUrl, lessonName, lessonId, onContinue }: VideoStageProps) => {
  const [hasEnded, setHasEnded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { playSfx } = useSfx();
  const unduckRef = useRef<((ms?: number) => void) | null>(null);
  const { playLessonMoment } = useLessonVoiceMoment();
  const postVideoFiredRef = useRef(false);
  const {
    enabled: captionsEnabled,
    setEnabled: setCaptionsEnabled,
    activeCue,
    hasCaptions,
    status: captionStatus,
  } = useLessonCaptions(lessonId, currentTime, duration);

  const release = () => {
    const fn = unduckRef.current;
    unduckRef.current = null;
    if (fn) fn(400);
  };
  const acquire = () => {
    if (unduckRef.current) return;
    unduckRef.current = musicController.pushDuck(0.4, 300);
  };
  useEffect(() => () => release(), []);

  const handlePlay = () => {
    videoRef.current?.play();
    setIsPlaying(true);
  };

  return (
    <div
      className="min-h-screen bg-background flex flex-col items-center justify-center gap-6 px-4"
    >
      <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground holo-text text-center drop-shadow-lg">
        {lessonName}
      </h2>

      {hasCaptions && (
        <div className="w-full max-w-3xl flex justify-end -mb-2">
          <button
            type="button"
            onClick={() => setCaptionsEnabled(!captionsEnabled)}
            aria-label="Toggle lesson video captions"
            aria-pressed={captionsEnabled}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/55 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold text-white hover:bg-black/70 transition-colors"
          >
            {captionsEnabled ? <Captions className="w-3.5 h-3.5" /> : <CaptionsOff className="w-3.5 h-3.5" />}
            <span>{captionsEnabled ? "CC On" : "CC Off"}</span>
          </button>
        </div>
      )}

      <div className="relative w-full max-w-3xl aspect-video rounded-2xl overflow-hidden border border-primary/20 shadow-glow">
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-contain bg-muted/70"
          onPlay={acquire}
          onPause={release}
          onEnded={() => {
            setHasEnded(true);
            release();
            // Post-video bridge: only after narration fully ends; once per lesson load.
            if (!postVideoFiredRef.current) {
              const moment = getLessonMomentFor(lessonId, "post_video");
              if (moment) {
                postVideoFiredRef.current = true;
                void playLessonMoment(moment.momentId);
              }
            }
          }}
          onTimeUpdate={(e) => setCurrentTime((e.target as HTMLVideoElement).currentTime)}
          onLoadedMetadata={(e) => setDuration((e.target as HTMLVideoElement).duration || 0)}
          controls={isPlaying}
          playsInline
        />
        {!isPlaying && (
          <button
            onClick={handlePlay}
            className="absolute inset-0 flex items-center justify-center bg-background/45 hover:bg-background/35 transition-colors"
            aria-label={`Play lesson video: ${lessonName}`}
          >
            <div className="w-20 h-20 rounded-full bg-accent/20 border-2 border-accent/40 flex items-center justify-center shadow-[0_0_30px_hsl(var(--accent)/0.3)]">
              <Play className="w-8 h-8 text-accent ml-1" />
            </div>
          </button>
        )}

        {captionsEnabled && hasCaptions && (
          <LessonClosedCaptions text={activeCue} />
        )}
      </div>

      {hasCaptions && captionStatus === "fallback" && captionsEnabled && (
        <p className="text-xs text-muted-foreground -mt-2">
          Live transcript (timed captions coming soon)
        </p>
      )}

      {hasEnded && (
        <Button
          onClick={() => { playSfx("soft_swoosh"); onContinue(); }}
          className="mt-4 px-8 py-3 bg-accent/15 border border-accent/30 text-accent font-heading font-semibold hover:bg-accent/25 shadow-[0_0_20px_hsl(var(--accent)/0.2)] animate-fade-in"
        >
          Continue →
        </Button>
      )}
    </div>
  );
};

export default VideoStage;
